import { Icon } from 'app/components/common/Icon';
import Widget from 'app/components/common/Widget';
import { useStore } from 'app/context';
import { NachfolgePlanungVm } from 'app/stores/ui/nachfolgeplanung.ui.store';
import { FocusThemaVM } from 'app/stores/ui/document.ui.store';
import { formatNum } from 'app/utils';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import Config from 'Config';
import { AgtNachfolgePlanungAgenturInfo } from './AgtNachfolgePlanungAgenturInfo';
import { AgtNachfolgePlanungBestandBewertung } from './AgtNachfolgePlanungBestandBewertung';
import { AgtNachfolgePlanungDigitalReport } from './AgtNachfolgePlanungDigitalReport';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';

interface FocusThemaCardProps {
	focusThema: FocusThemaVM;
	vm: NachfolgePlanungVm;
}

const FocusThemaCard = observer(({ focusThema, vm }: FocusThemaCardProps) => {
	const def = focusThema.def;
	if (!def) return null;

	const action = (
		<span className="tag" style={{ backgroundColor: def.background, color: def.color || 'inherit' }}>
			{def.themaShort}
		</span>
	);

	return (
		<Widget title={`Fokusthema ${Config.currentPlanYear}`} action={action} disableCollapse={true}>
			<div className="content">
				<p>
					<strong>{def.thema}</strong>

					{def.byLine && (
						<>
							<br />
							<span className="color-grey">{def.byLine}</span>
						</>
					)}
				</p>

				{focusThema.focusThema?.notiz && (
					<div>
						<strong>Notiz</strong>
						<br />
						<p>{focusThema.focusThema.notiz}</p>
					</div>
				)}

				{def.handlungsEmpfehlungen && def.handlungsEmpfehlungen.length > 0 && (
					<div>
						<strong>Handlungsempfehlungen</strong>
						<ul>
							{def.handlungsEmpfehlungen.map((emp, idx) => (
								<li key={idx}>{emp}</li>
							))}
						</ul>
					</div>
				)}
			</div>
		</Widget>
	);
});

interface AgtBestandsnachfolgeplanungProps {
	agtId?: number;
}

export const AgtBestandsnachfolgeplanung = observer(({ agtId: propAgtId }: AgtBestandsnachfolgeplanungProps = {}) => {
	const { nachfolgeplanungUiStore, berichteUiiStore, agenturUiStore } = useStore();
	const history = useHistoryWithQuery();
	const [vm, setVm] = useState<NachfolgePlanungVm | null>(null);

	// Use prop agtId if provided, otherwise fall back to store
	const agt = propAgtId
		? ({ agtId: propAgtId } as any) // We'll get the full agt object from vm
		: berichteUiiStore.selectedAgt || agenturUiStore.current;

	useEffect(() => {
		if (!agt) return;

		nachfolgeplanungUiStore.load().then(() => {
			const found = nachfolgeplanungUiStore.items.find((item) => item.agtId === agt.agtId);
			setVm(found || null);
		});
	}, [agt?.agtId, nachfolgeplanungUiStore]);

	if (!agt) {
		return (
			<div className="notification is-warning">
				<Icon iconClass="exclamation-triangle" /> Keine Agentur ausgewählt.
			</div>
		);
	}

	if (!vm) {
		return (
			<div className="notification is-info">
				<Icon iconClass="info-circle" /> Lade Daten für Bestandsnachfolgeplanung...
			</div>
		);
	}

	// Get all Nachfolgeplanung and Neugründung focus themes
	const focusThemen = vm.focusThemen;
	const displayAgt = vm.agt; // Always use the agency from vm for display

	return (
		<div className="pad-1rem full-height scroll-y">
			<div className="grid is-col-bigger-left align-start">
				{/* Left Column - Focus Themes */}
				<div className="grid">
					{focusThemen.length > 0 ? (
						focusThemen.map((ft) => <FocusThemaCard key={ft.def?.keyThema} focusThema={ft} vm={vm} />)
					) : (
						<Widget title="Fokusthemen" disableCollapse={true}>
							<div className="notification is-light">
								<Icon iconClass="info-circle" /> Keine Fokusthemen zur Nachfolgeplanung definiert.
							</div>
						</Widget>
					)}
				</div>

				{/* Right Column - Data Overview */}
				<div className="grid">
					{/* Agentur & Inhaber Info */}
					<AgtNachfolgePlanungAgenturInfo agtId={displayAgt.agtId} />

					{/* Bestand und Bewertung VJ */}
					<AgtNachfolgePlanungBestandBewertung agtId={displayAgt.agtId} vorJahr={Config.currentPlanYear - 1} />

					{/* Digital-Report VJ */}
					<AgtNachfolgePlanungDigitalReport agtId={displayAgt.agtId} />

					{/* Kunden Info */}

					<div className="table-container is-boxed">
						<table className="table">
							<thead>
								<tr>
									<th colSpan={2}>Kundenbestand</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td className="has-text-weight-semibold">Anzahl Kunden</td>
									<td>{formatNum(vm.anzKunden18)}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	);
});
